import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/GererateActivityForm.css';
import ErrorResponse from './ErrorResponse';
import { generateActivity } from '../services/generatorService';

const GenerateActivityForm = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    grade: '',
    subject: '',
    topics: [],
    skills: [],
  });

  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  // Age-appropriate topics by subject for elementary students
  const subjectTopics = {
    math: {
      kindergarten: [
        { value: 'counting', label: 'Counting 1-20' },
        { value: 'shapes', label: 'Basic Shapes' },
        { value: 'patterns', label: 'Simple Patterns' },
        { value: 'sorting', label: 'Sorting & Classifying' },
        { value: 'measurement', label: 'Size & Length' }
      ],
      '1st': [
        { value: 'addition', label: 'Addition Facts' },
        { value: 'subtraction', label: 'Subtraction Facts' },
        { value: 'place-value', label: 'Place Value' },
        { value: 'time', label: 'Telling Time' },
        { value: 'money', label: 'Coins & Money' },
        { value: 'measurement', label: 'Measurement' }
      ],
      '2nd': [
        { value: 'addition-regrouping', label: 'Addition with Regrouping' },
        { value: 'subtraction-regrouping', label: 'Subtraction with Regrouping' },
        { value: 'multiplication-intro', label: 'Introduction to Multiplication' },
        { value: 'fractions', label: 'Simple Fractions' },
        { value: 'data', label: 'Graphs & Data' }
      ],
      '3rd': [
        { value: 'multiplication', label: 'Multiplication Tables' },
        { value: 'division', label: 'Division Facts' },
        { value: 'fractions-equivalent', label: 'Equivalent Fractions' },
        { value: 'decimals', label: 'Introduction to Decimals' },
        { value: 'area-perimeter', label: 'Area & Perimeter' }
      ],
      '4th': [
        { value: 'multi-digit-operations', label: 'Multi-digit Operations' },
        { value: 'fractions-operations', label: 'Fraction Operations' },
        { value: 'decimals-operations', label: 'Decimal Operations' },
        { value: 'geometry-angles', label: 'Angles & Geometry' },
        { value: 'factors-multiples', label: 'Factors & Multiples' }
      ],
      '5th': [
        { value: 'fractions-mixed', label: 'Mixed Numbers & Improper Fractions' },
        { value: 'decimals-advanced', label: 'Advanced Decimals' },
        { value: 'volume', label: 'Volume & 3D Shapes' },
        { value: 'coordinate-plane', label: 'Coordinate Plane' },
        { value: 'algebra-prep', label: 'Pre-Algebra Concepts' }
      ]
    },
    science: {
      kindergarten: [
        { value: 'animals', label: 'Animals & Their Homes' },
        { value: 'plants', label: 'Plants & Growth' },
        { value: 'weather', label: 'Weather & Seasons' },
        { value: 'senses', label: 'Five Senses' },
        { value: 'magnets', label: 'Magnets' }
      ],
      '1st': [
        { value: 'life-cycles', label: 'Animal Life Cycles' },
        { value: 'plant-parts', label: 'Parts of a Plant' },
        { value: 'states-matter', label: 'Solids, Liquids, Gases' },
        { value: 'sound', label: 'Sound & Vibrations' },
        { value: 'light', label: 'Light & Shadows' }
      ],
      '2nd': [
        { value: 'habitats', label: 'Animal Habitats' },
        { value: 'food-chains', label: 'Simple Food Chains' },
        { value: 'properties-matter', label: 'Properties of Matter' },
        { value: 'simple-machines', label: 'Simple Machines' },
        { value: 'earth-materials', label: 'Rocks & Soil' }
      ],
      '3rd': [
        { value: 'ecosystems', label: 'Ecosystems' },
        { value: 'adaptations', label: 'Animal Adaptations' },
        { value: 'forces-motion', label: 'Forces & Motion' },
        { value: 'weather-climate', label: 'Weather vs Climate' },
        { value: 'space', label: 'Solar System' }
      ],
      '4th': [
        { value: 'energy', label: 'Forms of Energy' },
        { value: 'waves', label: 'Sound & Light Waves' },
        { value: 'earth-processes', label: 'Earth Processes' },
        { value: 'human-body', label: 'Human Body Systems' },
        { value: 'electricity', label: 'Simple Circuits' }
      ],
      '5th': [
        { value: 'matter-changes', label: 'Chemical vs Physical Changes' },
        { value: 'earth-systems', label: 'Earth Systems' },
        { value: 'stars-planets', label: 'Stars & Planets' },
        { value: 'engineering', label: 'Engineering Design' },
        { value: 'environment', label: 'Environmental Science' }
      ]
    },
    english: {
      kindergarten: [
        { value: 'alphabet', label: 'Letter Recognition' },
        { value: 'phonics', label: 'Letter Sounds' },
        { value: 'sight-words', label: 'Sight Words' },
        { value: 'rhyming', label: 'Rhyming Words' },
        { value: 'story-elements', label: 'Story Characters' }
      ],
      '1st': [
        { value: 'reading-comprehension', label: 'Reading Comprehension' },
        { value: 'sentence-structure', label: 'Complete Sentences' },
        { value: 'capitalization', label: 'Capitalization & Punctuation' },
        { value: 'descriptive-words', label: 'Descriptive Words' },
        { value: 'story-retelling', label: 'Story Retelling' }
      ],
      '2nd': [
        { value: 'reading-fluency', label: 'Reading Fluency' },
        { value: 'paragraph-writing', label: 'Paragraph Writing' },
        { value: 'grammar-basics', label: 'Nouns, Verbs, Adjectives' },
        { value: 'compare-contrast', label: 'Compare & Contrast' },
        { value: 'fiction-nonfiction', label: 'Fiction vs Nonfiction' }
      ],
      '3rd': [
        { value: 'reading-strategies', label: 'Reading Strategies' },
        { value: 'essay-writing', label: 'Opinion & Narrative Essays' },
        { value: 'grammar-advanced', label: 'Advanced Grammar' },
        { value: 'main-idea', label: 'Main Idea & Details' },
        { value: 'vocabulary', label: 'Context Clues' }
      ],
      '4th': [
        { value: 'literary-elements', label: 'Plot, Theme, Character' },
        { value: 'research-writing', label: 'Research & Reports' },
        { value: 'complex-sentences', label: 'Complex Sentences' },
        { value: 'text-features', label: 'Text Features' },
        { value: 'poetry', label: 'Poetry Analysis' }
      ],
      '5th': [
        { value: 'literary-analysis', label: 'Literary Analysis' },
        { value: 'persuasive-writing', label: 'Persuasive Writing' },
        { value: 'advanced-grammar', label: 'Advanced Grammar Rules' },
        { value: 'research-skills', label: 'Research & Citations' },
        { value: 'presentations', label: 'Oral Presentations' }
      ]
    },
    history: {
      kindergarten: [
        { value: 'community-helpers', label: 'Community Helpers' },
        { value: 'family-traditions', label: 'Family & Traditions' },
        { value: 'american-symbols', label: 'American Symbols' },
        { value: 'holidays', label: 'National Holidays' },
        { value: 'past-present', label: 'Past vs Present' }
      ],
      '1st': [
        { value: 'citizenship', label: 'Good Citizenship' },
        { value: 'rules-laws', label: 'Rules & Laws' },
        { value: 'historical-figures', label: 'Famous Americans' },
        { value: 'timeline', label: 'Personal Timelines' },
        { value: 'cultures', label: 'Different Cultures' }
      ],
      '2nd': [
        { value: 'native-americans', label: 'Native Americans' },
        { value: 'pilgrims', label: 'Pilgrims & Plymouth' },
        { value: 'colonial-life', label: 'Colonial Life' },
        { value: 'presidents', label: 'Famous Presidents' },
        { value: 'maps-geography', label: 'Maps & Geography' }
      ],
      '3rd': [
        { value: 'american-revolution', label: 'American Revolution' },
        { value: 'government', label: 'Local Government' },
        { value: 'economics', label: 'Needs vs Wants' },
        { value: 'immigration', label: 'Immigration Stories' },
        { value: 'civil-rights', label: 'Civil Rights Heroes' }
      ],
      '4th': [
        { value: 'state-history', label: 'State History' },
        { value: 'westward-expansion', label: 'Westward Expansion' },
        { value: 'civil-war', label: 'Civil War' },
        { value: 'industrial-revolution', label: 'Industrial Revolution' },
        { value: 'constitution', label: 'Constitution & Bill of Rights' }
      ],
      '5th': [
        { value: 'world-cultures', label: 'World Cultures' },
        { value: 'world-wars', label: 'World Wars' },
        { value: 'modern-history', label: 'Modern American History' },
        { value: 'government-branches', label: 'Branches of Government' },
        { value: 'economics-advanced', label: 'Economics & Trade' }
      ]
    },
    geography: [
      { value: 'maps-globes', label: 'Maps & Globes' },
      { value: 'continents-oceans', label: 'Continents & Oceans' },
      { value: 'landforms', label: 'Landforms' },
      { value: 'countries', label: 'Countries & Capitals' },
      { value: 'climate-zones', label: 'Climate Zones' }
    ],
    art: [
      { value: 'drawing', label: 'Drawing Techniques' },
      { value: 'coloring', label: 'Coloring Activities' },
      { value: 'tracing', label: 'Tracing & Copying' },
      { value: 'painting', label: 'Painting & Colors' },
      { value: 'sculpture', label: 'Sculpture & 3D Art' },
      { value: 'famous-artists', label: 'Famous Artists' },
      { value: 'art-history', label: 'Art Through History' }
    ],
    music: [
      { value: 'rhythm', label: 'Rhythm & Beat' },
      { value: 'instruments', label: 'Musical Instruments' },
      { value: 'composers', label: 'Famous Composers' },
      { value: 'genres', label: 'Musical Genres' },
      { value: 'reading-music', label: 'Reading Music Notes' }
    ],
    'physical-ed': [
      { value: 'fitness', label: 'Health & Fitness' },
      { value: 'team-sports', label: 'Team Sports' },
      { value: 'individual-sports', label: 'Individual Sports' },
      { value: 'nutrition', label: 'Nutrition & Healthy Eating' },
      { value: 'safety', label: 'Safety & First Aid' }
    ],
    finance: [
      { value: 'money-basics', label: 'What is Money?' },
      { value: 'earning', label: 'Ways to Earn Money' },
      { value: 'spending', label: 'Smart Spending' },
      { value: 'saving', label: 'Saving Money' },
      { value: 'budgeting', label: 'Simple Budgets' },
      { value: 'investing', label: 'Introduction to Investing' },
      { value: 'entrepreneurship', label: 'Entrepreneurship' }
    ],
    coding: [
      { value: 'algorithms', label: 'Step-by-Step Instructions' },
      { value: 'loops', label: 'Patterns & Loops' },
      { value: 'debugging', label: 'Finding & Fixing Mistakes' },
      { value: 'block-coding', label: 'Visual Block Programming' },
      { value: 'digital-citizenship', label: 'Digital Citizenship' },
      { value: 'web-development', label: 'Basic Web Development' },
      { value: 'game-design', label: 'Simple Game Design' }
    ],
    spanish: [
      { value: 'greetings', label: 'Greetings & Introductions' },
      { value: 'numbers', label: 'Numbers & Counting' },
      { value: 'colors', label: 'Colors' },
      { value: 'family', label: 'Family Members' },
      { value: 'animals', label: 'Animals' },
      { value: 'food', label: 'Food & Meals' },
      { value: 'conjugation', label: 'Basic Verb Conjugation' }
    ]
  };

  const gradeLevels = [
    { value: '', label: 'Select Grade Level' },
    { value: 'kindergarten', label: 'Kindergarten' },
    { value: '1st', label: '1st Grade' },
    { value: '2nd', label: '2nd Grade' },
    { value: '3rd', label: '3rd Grade' },
    { value: '4th', label: '4th Grade' },
    { value: '5th', label: '5th Grade' },
  ];

  const subjects = [
    { value: '', label: 'Select Subject' },
    { value: 'math', label: 'Mathematics' },
    { value: 'science', label: 'Science' },
    { value: 'english', label: 'English/Language Arts' },
    { value: 'finance', label: 'Finance' },
    { value: 'history', label: 'History/Social Studies' },
    { value: 'coding', label: 'Coding/Computer Science' },
    { value: 'art', label: 'Art' },
    { value: 'music', label: 'Music' },
    { value: 'physical-ed', label: 'Physical Education' },
    { value: 'geography', label: 'Geography' },
    { value: 'spanish', label: 'Spanish' }
  ];

  const skills = [
    { value: '', label: 'Select Skill' },
    { value: 'critical-thinking', label: 'Critical Thinking' },
    { value: 'problem-solving', label: 'Problem Solving' },
    { value: 'creativity', label: 'Creativity' },
    { value: 'collaboration', label: 'Collaboration' },
    { value: 'communication', label: 'Communication' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'time-management', label: 'Time Management' },
    { value: 'adaptability', label: 'Adaptability' },
  ];

  // Get available topics based on selected subject and grade
  const getAvailableTopics = () => {
    if (!formData.subject) return [];
    
    const subjectData = subjectTopics[formData.subject];
    if (!subjectData) return [];
    
    // If subject has grade-specific topics, use them
    if (typeof subjectData === 'object' && !Array.isArray(subjectData)) {
      return subjectData[formData.grade] || [];
    }
    
    // If subject has general topics (not grade-specific), return them
    return Array.isArray(subjectData) ? subjectData : [];
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle multi-select for skills and topics
    if (name === 'skills' || name === 'topics') {
      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
      setFormData({
        ...formData,
        [name]: selectedOptions,
      });
    } else {
      // Reset topics when subject or grade changes
      const newFormData = {
        ...formData,
        [name]: value,
      };
      
      if (name === 'subject' || name === 'grade') {
        newFormData.topics = []; // Reset topics when subject/grade changes
      }
      
      setFormData(newFormData);
    }
  }; 

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Double-check authentication before submitting
    if (!isAuthenticated) {
      setError('Please log in to generate activities.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      // Prepare data for API
      // Use current timestamp as the activity date since user doesn't specify
      const payload = {
        ...formData,
        // ISO 8601 timestamp of when this record is submitted/created
        timestamp: new Date().toISOString(),
      };

      // Call the authenticated generate activity service
      const result = await generateActivity(payload);

      setResponse(result);
      
      // Reset form on success
      setFormData({
        title: '',
        grade: '', 
        subject: '',
        topics: [],
        skills: [],
      });
    } catch (err) {
      console.error('Error submitting form:', err);
      if (err.message.includes('Authentication failed')) {
        setError('Please log in again to generate activities.');
      } else {
        setError(
          err.message || 
          'Failed to generate activity. Please try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="homeschool-form-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  // Require authentication to access the form
  if (!isAuthenticated) {
    return (
      <div className="homeschool-form-container">
        <div className="auth-required">
          <h3>Authentication Required</h3>
          <p>Please log in to generate homeschool activities.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="homeschool-form-container">
      <h1>Homeschool Summer Activity Generator</h1>
      <p className="subtitle">Submit parameters to develop a prompt for the type of learning activity you want to generate.</p>

      <form onSubmit={handleSubmit} className="homeschool-form">

        {/* Activity Title */}
        <div className="form-group">
          <label htmlFor="title">Activity Title</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="e.g. Emma's Multiplication Practice"
            maxLength={100}
          />
          <small className="help-text">Give this worksheet a name you'll recognise in your history (optional).</small>
        </div>

        {/* Grade Level Dropdown */}
        <div className="form-group">
          <label htmlFor="grade">Grade Level *</label>
          <select
            id="grade"
            name="grade"
            value={formData.grade}
            onChange={handleInputChange}
            required
          >
            {gradeLevels.map((level) => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
        </div>

        {/* Subject Dropdown */}
        <div className="form-group">
          <label htmlFor="subject">Subject *</label>
          <select
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleInputChange}
            required
          >
            {subjects.map((subj) => (
              <option key={subj.value} value={subj.value}>
                {subj.label}
              </option>
            ))}
          </select>
        </div>

        {/* Topics Multi-select */}
        {formData.subject && getAvailableTopics().length > 0 && (
          <div className="form-group">
            <label htmlFor="topics">Topics {formData.grade && `(${formData.grade} grade)`}</label>
            <select
              id="topics"
              name="topics"
              value={formData.topics}
              onChange={handleInputChange}
              multiple
              size="6"
              className="multi-select"
            >
              {getAvailableTopics().map((topic) => (
                <option key={topic.value} value={topic.value}>
                  {topic.label}
                </option>
              ))}
            </select>
            <small className="help-text">
              Hold Ctrl (Cmd on Mac) to select multiple topics. Topics are filtered by grade level for age-appropriate content.
            </small>
          </div>
        )}

        {/* Prompt - Long Text Box */}
{/*         <div className="form-group">
          <label htmlFor="prompt">Prompt*</label>
          <textarea
            id="prompt"
            name="prompt"
            value={formData.prompt}
            onChange={handleInputChange}
            required
            placeholder="Describe the learning activity in detail in the form of a prompt for Generative AI foundation model"
            rows="6"
          />
        </div> */}

        {/*  Skills multi-select */}
        <div className="form-group">
          <label htmlFor="skills">Skills/Learning Objectives</label>
          <select
            id="skills"
            name="skills"
            value={formData.skills}
            onChange={handleInputChange}
            multiple
            size="5"
            className="multi-select"
          >
            {skills.slice(1).map((skill) => ( // Fixed: Skip the first "Select Skill" option for multi-select
              <option key={skill.value} value={skill.value}>
                {skill.label}
              </option>
            ))}
          </select>
          <small className="help-text">Hold Ctrl (Cmd on Mac) to select multiple skills</small>
        </div>

        {/* Submit Button */}
        <div className="form-group">
          <button type="submit" disabled={loading} className="submit-button">
            {loading ? 'Submitting...' : 'Submit Activity'}
          </button>
        </div>
      </form>

      {/* Success Message */}
      {response && (
        <div className="success-message">
          <h3>✓ Activity submitted successfully!</h3>
          <pre>{JSON.stringify(response, null, 2)}</pre>
        </div>
      )}
      
      <ErrorResponse 
        error={error ? { 
          message: 'Failed to generate activity',
          details: error
        } : null}
        onRetry={() => {
          if (isAuthenticated) {
            setError(null);
            handleSubmit(new Event('submit'));
          }
        }}
        onDismiss={() => {
          setError(null);
        }}
      />
    </div>
  );
};

export default GenerateActivityForm;